import React from 'react';
import { formatCurrency } from '@/lib/helpers/strings';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { useAuthModal } from 'modules/auth';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/lib/components/ui/card';
import { Button } from '@/lib/components/ui/button';
import { Badge } from '@/lib/components/ui/badge';
import { Euro } from 'lucide-react';
import dayjs from 'dayjs';
import { experiences } from 'modules/User/models/constants';
import { MdFolderSpecial } from 'react-icons/md';
import useUser from 'modules/User/hooks/useUser';
import router from 'next/router';
import classNames from 'classnames';
import { AvailableProfile } from '../models/AvailableProfile';
import HeadHunterAvatar from './HeadHunterAvatar';

type CVThequeProps = React.HTMLAttributes<HTMLDivElement> & {
  availableProfiles: AvailableProfile[];
  maxItems?: number;
};

const CVTheque: React.FC<CVThequeProps> = ({ children, className, availableProfiles, maxItems = 12, ...props }) => {
  const { toggleModal } = useAuthModal();
  const { user } = useUser();
  const [allShown, setAllShow] = React.useState(false);

  return (
    <div className={classNames('container mx-auto py-8', className)} {...props}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {availableProfiles.map((availableProfile, index) => (
          <Card key={availableProfile._id} className={classNames('flex flex-col justify-between', index >= maxItems && !allShown && 'hidden')}>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2 justify-between mb-3">
                  <HeadHunterAvatar
                    user={{
                      name: availableProfile.name,
                    }}
                    withCompany={false}
                  />

                  <Badge variant="outline-primary" size="lg">
                    {formatCurrency({ value: availableProfile.bounty, currency: availableProfile.currency })}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {availableProfile.occupations.length === 0 && <Badge variant="outline">Tous métiers</Badge>}
                    {availableProfile.occupations?.map((occupation: any) => (
                      <Badge key={occupation._id} variant="outline">
                        {occupation.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardTitle>
              <CardDescription>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <CountryEmojis countryIds={availableProfile.countryIds} />
                  {availableProfile.sectors.length === 0 && <Badge variant="outline">Tous secteurs</Badge>}
                  {availableProfile.sectors?.map((sector: any) => (
                    <Badge key={sector._id} variant="outline">
                      {sector.name}
                    </Badge>
                  ))}
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <Areas items={availableProfile.areaSearch?.areas || []} className="!text-base" />
                </div>

                <div className="flex items-center text-gray-600">
                  <Euro className="h-4 w-4 mr-2" />
                  <span>
                    {availableProfile.baseSalary.min.toLocaleString('fr-FR')} - {availableProfile.baseSalary.max.toLocaleString('fr-FR')} €
                  </span>
                </div>

                <div className="flex items-center text-gray-600">
                  <MdFolderSpecial className="h-4 w-4 mr-2" />
                  <span>{(experiences as any)[availableProfile?.experience] || availableProfile.experience}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end flex-col gap-0.5 items-end bg-gray-50 py-3 px-6">
              <Button
                variant="accent"
                onClick={() => {
                  if (user) {
                    router.push(`/available-profiles/${availableProfile._id}`);
                  } else {
                    toggleModal();
                  }
                }}
              >
                Voir le candidat Off-Market
              </Button>
              <div className="text-[10px] text-gray-500" suppressHydrationWarning>
                Publié(e) {dayjs(availableProfile.publishedAt).fromNow()}
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
      {availableProfiles.length > maxItems && (
        <div className="flex justify-center mt-12 sticky bottom-3 bg-white/80 py-2">
          <Button variant="primary" onClick={() => setAllShow(!allShown)} size="lg">
            {allShown ? (
              'Voir moins'
            ) : (
              <>
                Voir <strong>{availableProfiles.length - maxItems}</strong> autres candidats
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CVTheque;
